import type {
  SalesByDayResponseType,
  SalesReportResponseType,
  SalesReportSale,
} from '@aeris/contracts'
import { formatMoney, parseHumanAmount } from '@aeris/money'
import {
  tableReportPrintFor,
  type PrintDocument,
  type TableReportInput,
} from '@aeris/printing'

import { formatDate, formatDayTime } from './date-format.ts'
import {
  numberValue,
  textValue,
  type ExportColumn,
} from './export/table-export.ts'

/**
 * As vendas do período no papel e na planilha — F271.
 *
 * Fora do componente pelo Fast Refresh (como `report-search.ts`) e para o
 * teste montar o documento sem tela.
 */

export type SalesReportMode = 'detalhado' | 'consolidado'

export const SALES_REPORT_MODES: readonly SalesReportMode[] = [
  'detalhado',
  'consolidado',
]

/** Dinheiro do fio, escrito em reais. Nunca passa por `Number` (ADR-0016). */
export const reais = (raw: string): string => {
  const amount = parseHumanAmount(raw)
  return amount === null ? raw : formatMoney(amount)
}

/** `1.000000` → `1`; `0.500000` → `0,5`. */
export const pieces = (raw: string): string =>
  raw.replace(/\.?0+$/, '').replace('.', ',')

/** `2026-10-07` → `07/10/2026`, sem passar por fuso. */
export const dayLabel = (day: string): string => {
  const [year, month, date] = day.split('-')
  return `${date ?? ''}/${month ?? ''}/${year ?? ''}`
}

const WEEKDAYS = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb']

const utcOf = (day: string): Date => new Date(`${day}T12:00:00Z`)

/** `2026-10-07` → `qua`. */
export const weekdayOf = (day: string): string =>
  WEEKDAYS[utcOf(day).getUTCDay()] ?? ''

/** A segunda-feira da semana do dia, `AAAA-MM-DD`. */
export const mondayOf = (day: string): string => {
  const at = utcOf(day)
  const back = (at.getUTCDay() + 6) % 7
  at.setUTCDate(at.getUTCDate() - back)
  return at.toISOString().slice(0, 10)
}

const sumMoney = (values: readonly string[]): string => {
  // Em centavos inteiros: somar o texto como float perderia o centavo.
  const cents = values.reduce((total, value) => {
    const [whole = '0', fraction = ''] = value.replace('-', '').split('.')
    const amount =
      BigInt(whole) * 100n + BigInt((fraction + '00').slice(0, 2) || '0')
    return total + (value.startsWith('-') ? -amount : amount)
  }, 0n)
  const negative = cents < 0n
  const absolute = negative ? -cents : cents
  return `${negative ? '-' : ''}${String(absolute / 100n)}.${String(absolute % 100n).padStart(2, '0')}`
}

export type SalesWeek = {
  readonly monday: string
  readonly days: SalesByDayResponseType['days']
  readonly sales: number
  readonly discount: string
  readonly total: string
}

/** "Tipo semanal": os dias juntados pela semana (segunda a domingo). */
export const weeksOf = (days: SalesByDayResponseType['days']): SalesWeek[] => {
  const weeks = new Map<string, SalesByDayResponseType['days'][number][]>()
  for (const day of days) {
    const key = mondayOf(day.day)
    weeks.set(key, [...(weeks.get(key) ?? []), day])
  }
  return [...weeks.entries()].map(([monday, list]) => ({
    monday,
    days: list,
    sales: list.reduce((total, day) => total + day.sales, 0),
    discount: sumMoney(list.map((day) => day.discount)),
    total: sumMoney(list.map((day) => day.total)),
  }))
}

const sundayOf = (monday: string): string => {
  const at = utcOf(monday)
  at.setUTCDate(at.getUTCDate() + 6)
  return at.toISOString().slice(0, 10)
}

export const weekLabel = (week: SalesWeek): string =>
  `Semana de ${dayLabel(week.monday)} a ${dayLabel(sundayOf(week.monday))}`

/** "Venda nº 1234 · 07/10 14:32 · Ana · Pix". */
export const saleTitle = (sale: SalesReportSale): string =>
  [
    sale.number === null ? 'Venda' : `Venda nº ${sale.number}`,
    formatDayTime(sale.issuedAt),
    sale.salespersonName,
    sale.payments.length === 0
      ? null
      : sale.payments
          .map((payment) =>
            sale.payments.length === 1
              ? payment.name
              : `${payment.name} ${reais(payment.amount)}`
          )
          .join(' + '),
  ]
    .filter((part): part is string => part !== null && part !== '')
    .join(' · ')

const pieceCount = (sales: readonly SalesReportSale[]): string =>
  pieces(
    sales
      .flatMap((sale) => sale.lines.map((line) => Number(line.quantity)))
      .reduce((total, value) => total + value, 0)
      .toFixed(6)
  )

/** Um produto que o cliente levou no período, somado de todas as vendas. */
export type BoughtItem = {
  readonly description: string
  readonly sku: string | null
  readonly quantity: string
  readonly total: string
  /** Em quantas vendas ele apareceu. */
  readonly sales: number
}

/**
 * O que o cliente mais compra — F299.
 *
 * O dono, olhando o top clientes: "pra saber o que ele comprou no mês, o que
 * ele mais compra, pra fazer uma base do que posso enfiar mais nele". Somado
 * aqui, das mesmas vendas que a lista mostra: o resumo não tem como discordar
 * das peças embaixo dele. Mais peças primeiro; empate, mais dinheiro.
 */
export const mostBought = (sales: readonly SalesReportSale[]): BoughtItem[] => {
  const items = new Map<
    string,
    {
      description: string
      sku: string | null
      quantity: number
      totals: string[]
      sales: Set<string>
    }
  >()
  for (const sale of sales) {
    for (const line of sale.lines) {
      const key = line.sku ?? `~${line.description}`
      const item = items.get(key) ?? {
        description: line.description,
        sku: line.sku,
        quantity: 0,
        totals: [],
        sales: new Set<string>(),
      }
      item.quantity += Number(line.quantity)
      item.totals.push(line.total)
      item.sales.add(sale.id)
      items.set(key, item)
    }
  }
  return [...items.values()]
    .map((item) => ({
      description: item.description,
      sku: item.sku,
      quantity: item.quantity.toFixed(6),
      total: sumMoney(item.totals),
      sales: item.sales.size,
    }))
    .sort(
      (a, b) =>
        Number(b.quantity) - Number(a.quantity) ||
        Number(b.total) - Number(a.total) ||
        a.description.localeCompare(b.description, 'pt-BR')
    )
}

type Header = Pick<TableReportInput, 'company' | 'template'> & {
  readonly from: string
  /** O último dia, inclusivo — o que a pessoa escolheu. */
  readonly to: string
  readonly branchName: string
  readonly customerName: string | null
}

const filtersOf = (header: Header): TableReportInput['filters'] => [
  {
    label: 'Período',
    value: `${formatDate(header.from)} a ${formatDate(header.to)}`,
  },
  { label: 'Filial', value: header.branchName },
  ...(header.customerName === null
    ? []
    : [{ label: 'Cliente', value: header.customerName }]),
]

/**
 * O relatório detalhado: uma seção por venda, com as peças e o total dela;
 * o resumo em cima leva o valor total do período.
 */
export const salesDetailPrint = (
  header: Header,
  page: SalesReportResponseType
): PrintDocument =>
  tableReportPrintFor({
    company: header.company,
    template: header.template,
    title: 'Vendas do período',
    filters: filtersOf(header),
    summary: [
      { label: 'Vendas', value: String(page.totals.sales) },
      { label: 'Peças', value: pieceCount(page.sales) },
      { label: 'Total bruto', value: reais(page.totals.subtotal) },
      { label: 'Descontos', value: reais(page.totals.discount) },
      { label: 'Valor total', value: reais(page.totals.total), bold: true },
    ],
    notes: page.truncated
      ? [
          `Lista cortada em ${String(page.sales.length)} vendas: escolha um período menor. O resumo acima é do período inteiro.`,
        ]
      : [],
    sections: [
      // F299: de um cliente só, o que ele mais compra vem antes das vendas.
      ...(header.customerName === null || page.sales.length === 0
        ? []
        : [
            {
              title: 'O que mais compra',
              columns: [
                { label: 'Produto' },
                { label: 'Qtd', align: 'right' as const },
                { label: 'Vendas', align: 'right' as const },
                { label: 'Total', align: 'right' as const },
              ],
              rows: mostBought(page.sales).map((item) => [
                item.sku === null
                  ? item.description
                  : `${item.description} (${item.sku})`,
                pieces(item.quantity),
                String(item.sales),
                reais(item.total),
              ]),
            },
          ]),
      ...page.sales.map((sale) => ({
        title:
          header.customerName === null
            ? `${saleTitle(sale)} · ${sale.customerName}`
            : saleTitle(sale),
        columns: [
          { label: 'Produto' },
          { label: 'Qtd', align: 'right' as const },
          { label: 'Unitário', align: 'right' as const },
          { label: 'Desconto', align: 'right' as const },
          { label: 'Total', align: 'right' as const },
        ],
        rows: sale.lines.map((line) => [
          line.sku === null
            ? line.description
            : `${line.description} (${line.sku})`,
          pieces(line.quantity),
          reais(line.unitPrice),
          line.discount === '0.00' ? '—' : reais(line.discount),
          reais(line.total),
        ]),
        footer: ['Total da venda', '', '', '', reais(sale.total)],
      })),
    ],
  })

/** O consolidado: uma seção por semana, um dia por linha. */
export const salesByDayPrint = (
  header: Header,
  page: SalesByDayResponseType
): PrintDocument =>
  tableReportPrintFor({
    company: header.company,
    template: header.template,
    title: 'Vendas do período — consolidado',
    filters: filtersOf(header),
    summary: [
      { label: 'Vendas', value: String(page.totals.sales) },
      { label: 'Total bruto', value: reais(page.totals.subtotal) },
      { label: 'Descontos', value: reais(page.totals.discount) },
      { label: 'Valor total', value: reais(page.totals.total), bold: true },
      ...page.payments.map((payment) => ({
        label: payment.name,
        value: reais(payment.amount),
      })),
    ],
    sections: weeksOf(page.days).map((week) => ({
      title: weekLabel(week),
      columns: [
        { label: 'Dia' },
        { label: 'Vendas', align: 'right' as const },
        { label: 'Descontos', align: 'right' as const },
        { label: 'Total', align: 'right' as const },
      ],
      rows: week.days.map((day) => [
        `${weekdayOf(day.day)} ${dayLabel(day.day)}`,
        String(day.sales),
        reais(day.discount),
        reais(day.total),
      ]),
      footer: [
        'Total da semana',
        String(week.sales),
        reais(week.discount),
        reais(week.total),
      ],
    })),
  })

/** Uma linha da planilha detalhada: uma peça, com a venda dela. */
export type SalesExportRow = {
  readonly sale: SalesReportSale
  readonly line: SalesReportSale['lines'][number]
}

export const salesExportRows = (
  page: SalesReportResponseType
): SalesExportRow[] =>
  page.sales.flatMap((sale) => sale.lines.map((line) => ({ sale, line })))

export const SALES_EXPORT_COLUMNS: readonly ExportColumn<SalesExportRow>[] = [
  { id: 'venda', label: 'Venda', value: (row) => textValue(row.sale.number) },
  {
    id: 'data',
    label: 'Data',
    value: (row) => textValue(formatDayTime(row.sale.issuedAt)),
  },
  {
    id: 'cliente',
    label: 'Cliente',
    value: (row) => textValue(row.sale.customerName),
  },
  {
    id: 'documento',
    label: 'CPF/CNPJ',
    value: (row) => textValue(row.sale.customerDocument),
  },
  {
    id: 'vendedor',
    label: 'Vendedor',
    value: (row) => textValue(row.sale.salespersonName),
  },
  {
    id: 'pagamento',
    label: 'Pagamento',
    value: (row) =>
      textValue(row.sale.payments.map((payment) => payment.name).join(' + ')),
  },
  {
    id: 'produto',
    label: 'Produto',
    value: (row) => textValue(row.line.description),
  },
  { id: 'codigo', label: 'Código', value: (row) => textValue(row.line.sku) },
  {
    id: 'quantidade',
    label: 'Quantidade',
    value: (row) => numberValue(row.line.quantity),
  },
  {
    id: 'unitario',
    label: 'Unitário',
    value: (row) => numberValue(row.line.unitPrice),
  },
  {
    id: 'desconto',
    label: 'Desconto',
    value: (row) => numberValue(row.line.discount),
  },
  { id: 'total', label: 'Total', value: (row) => numberValue(row.line.total) },
  {
    id: 'total_venda',
    label: 'Total da venda',
    value: (row) => numberValue(row.sale.total),
  },
]

export type SalesDayRow = SalesByDayResponseType['days'][number]

export const SALES_BY_DAY_EXPORT_COLUMNS: readonly ExportColumn<SalesDayRow>[] =
  [
    { id: 'dia', label: 'Dia', value: (row) => textValue(dayLabel(row.day)) },
    {
      id: 'vendas',
      label: 'Vendas',
      value: (row) => numberValue(String(row.sales)),
    },
    {
      id: 'bruto',
      label: 'Total bruto',
      value: (row) => numberValue(row.subtotal),
    },
    {
      id: 'desconto',
      label: 'Descontos',
      value: (row) => numberValue(row.discount),
    },
    { id: 'total', label: 'Total', value: (row) => numberValue(row.total) },
  ]
